import { appStore } from '@/store/app'
import {
  Cartesian3,
  Ellipsoid,
  HeadingPitchRoll,
  HeadingPitchRange,
  CallbackProperty,
  PositionProperty,
  Transforms,
  Matrix4,
  RuntimeError,
  CallbackPositionProperty,
  PolylineDashMaterialProperty,
  PolylineGlowMaterialProperty,
  Color,
  ShadowMode,
  ConstantProperty,
  Viewer,
  Math as CesiumMath,
  SampledPositionProperty,
  JulianDate,
  TimeInterval,
} from 'cesium'
import type { Ref } from 'vue'
import { CesiumService } from '@/handlers/cesium.service'
import { FPVService } from '@/handlers/fpv.service'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { makeSerializableForWorkers } from '@/utils/cesiumUtils'
import { cesiumConfig, getDroneModel } from '@/config/cesium.config'
import useDroneLocation from './useDroneLocation'
import { useMissionsState } from '@/composables/Missions/useMissionsState'
import { useMapFunctions } from '../useMapFunctions'
import { onEvent } from '@/utils/EventBus'
import type { TelemetryData } from '@/types/dashboard.types'
import { cameraStore } from '@/store/camera'
import { useAssetManager } from './useAssetManager'
import log from '@/utils/useLogger'
import { labelStyle } from '@/constants/appData'
import { useElevation } from './useElevation'
import { createGroupLifecycle, isAbortError } from './useGroupLifecycle'

// Update the interface to use string IDs
interface IDroneEntityUpdated {
  droneEntity: Ref<string | null>
  createDroneEntity: () => Promise<void>
  initializeForGroup: (groupId: string) => Promise<void>
  setupDroneTracking: () => (() => void) | undefined
  showFPV: Ref<boolean>
  initializeFPV: (fpvElement: Ref<HTMLElement | null>) => Promise<void>
  fpvViewer: Ref<Viewer | null>
  toggleFpvTileset: (show: boolean) => Promise<boolean>
  cleanup: (opts?: { unmount?: boolean }) => Promise<void>
}

/**
 * Module-scope so the service survives device-group swaps. Paired with
 * `useAddAssets`'s lifecycle but fully independent — drone telemetry can
 * update while assets are still swapping.
 */
const droneLifecycle = createGroupLifecycle('drone')
const moduleCleanupFunctions: Array<() => void> = []
let trackingCleanup: (() => void) | null = null
let droneWatchersInitialized = false
let droneInitInFlight: Promise<void> | null = null

export const useDroneEntity = (): IDroneEntityUpdated => {
  const appInstance = appStore()
  const assetManager = useAssetManager()
  const { droneEntity, connectingLine, altitudeLine, trailingPathEntity } = assetManager
  const { telemetry, droneAmsl, dockConfigurations, dockAmsl } = storeToRefs(appInstance)
  const { beginPlot, endPlot } = assetManager
  const { missions } = useMissionsState()
  const { droneLocation } = useDroneLocation()
  const { isTrackingDrone } = storeToRefs(cameraStore())
  const { removeEntityIfExists } = useMapFunctions()
  const { droneAGL, setAmsl, AGL } = useElevation()

  /** Trail time in seconds: when a mission is running (from telemetry runningMissionID), use that mission's estimated duration + buffer; else config default. Capped by maxTrailTime. */
  const getEffectiveTrailTime = (): number => {
    const cfg = cesiumConfig.dronePath
    const defaultSeconds = cfg.trailTime
    const maxSeconds = cfg.maxTrailTime ?? 14400
    const buffer = cfg.trailTimeBuffer ?? 300
    const runningMissionID = telemetry.value?.missionWaypoints?.missions?.runningMissionID
    if (runningMissionID) {
      const runningMission = missions.value.find((m) => m.missionId === runningMissionID)
      const missionSeconds = runningMission?.areaMission?.stats?.estimatedTime
      if (typeof missionSeconds === 'number' && missionSeconds > 0) {
        const desired = missionSeconds + buffer
        const effectiveTrailTime = Math.min(maxSeconds, Math.max(defaultSeconds, desired))
        return effectiveTrailTime
      }
    }
    return defaultSeconds
  }

  const hpRoll = new HeadingPitchRoll()
  const fpvViewer = computed(() => FPVService.fpvViewer.value)
  const viewer = computed(() => CesiumService.viewer.value)

  // SampledPositionProperty for storing historical positions for the trailing path
  let sampledPositionProperty: SampledPositionProperty | null = null

  const showFPV = computed({
    get: () => FPVService.showFPV.value,
    set: (value) => {
      FPVService.showFPV.value = value
    },
  })

  // Idempotent: only attach the map-ready listener the first time any caller
  // instantiates the composable. Subsequent calls reuse the existing listener.
  if (moduleCleanupFunctions.length === 0) {
    const mapReadyCleanup = onEvent('map-ready', () => {
      if (viewer.value && droneEntity.value) {
        setupWatchers()
      }
    })
    moduleCleanupFunctions.push(mapReadyCleanup)
  }

  const createDroneEntity = async () => {
    const gen = droneLifecycle.generation
    beginPlot()
    try {
      if (!viewer.value) return
      if (droneEntity.value) {
        removeEntityIfExists(droneEntity.value)
        droneEntity.value = null
      }
      if (!droneAmsl.value) {
        await setAmsl({ signal: droneLifecycle.signal })
        if (droneLifecycle.isStale(gen)) return
      }

      const position = new CallbackPositionProperty(() => {
        const position = Cartesian3.fromDegrees(
          droneLocation.value.longitude,
          droneLocation.value.latitude,
          droneAGL.value,
        )
        return position
      }, false)

      // Local frame transform for proper orientation
      const fixedFrameTransform = Transforms.localFrameToFixedFrameGenerator('north', 'west')

      // Model (uri + size) resolved from the selected device type (FLY3D-965)
      const droneModelCfg = getDroneModel(appInstance.selectedRCObj?.deviceType)

      // Create drone entity with proper ground positioning
      const entity = viewer.value.entities.add({
        position: new CallbackProperty(() => {
          // Use current position from telemetry if available
          return position.getValue(CesiumService.viewer.value!.clock.currentTime)
        }, false) as unknown as PositionProperty,
        orientation: new CallbackProperty(() => {
          // Update heading, pitch, roll from telemetry
          const h = telemetry.value?.aircraftState?.heading
          if (typeof h === 'number') hpRoll.heading = CesiumMath.toRadians(h)
          const p = telemetry.value?.aircraftState?.attitude?.pitch
          if (typeof p === 'number') hpRoll.pitch = CesiumMath.toRadians(p)
          const r = telemetry.value?.aircraftState?.attitude?.roll
          if (typeof r === 'number') hpRoll.roll = CesiumMath.toRadians(r)

          const currentPosition = position.getValue(CesiumService.viewer.value!.clock.currentTime)

          if (!currentPosition) {
            return Transforms.headingPitchRollQuaternion(
              Cartesian3.ZERO,
              hpRoll,
              Ellipsoid.WGS84,
              fixedFrameTransform,
            )
          }

          return Transforms.headingPitchRollQuaternion(
            currentPosition,
            hpRoll,
            Ellipsoid.WGS84,
            fixedFrameTransform,
          )
        }, false),
        model: {
          uri: droneModelCfg.uri,
          minimumPixelSize: droneModelCfg.minimumPixelSize,
          maximumScale: cesiumConfig.droneModel.maximumScale,
          scale: droneModelCfg.scale,
          runAnimations: new ConstantProperty(telemetry.value?.aircraftState?.motorON || false),
          clampAnimations: cesiumConfig.droneModel.clampAnimations,
          incrementallyLoadTextures: cesiumConfig.droneModel.incrementallyLoadTextures,
          shadows: cesiumConfig.droneModel.shadows ? ShadowMode.ENABLED : ShadowMode.DISABLED,
          silhouetteSize: cesiumConfig.droneModel.silhouetteSize,
        },
      })

      droneEntity.value = entity.id

      // Set up animation watcher
      watch(
        () => telemetry.value?.aircraftState?.motorON,
        (motorON) => {
          if (droneEntity.value && viewer.value) {
            const entity = viewer.value.entities.getById(droneEntity.value)
            if (entity?.model) {
              entity.model.runAnimations = new ConstantProperty(motorON || false)
            }
          }
        },
      )
      connectAltitudeLine(position)
      connectHomeMarker()
      initializeTrailingPath()
      // Drone tracking + watchers persist across swaps — install once.
      if (!trackingCleanup) {
        trackingCleanup = setupDroneTracking() ?? null
      }
      if (!droneWatchersInitialized) {
        setupWatchers()
        droneWatchersInitialized = true
      }
    } catch (err) {
      if (isAbortError(err)) return
      log.error('error creating drone entity', err)
    } finally {
      endPlot()
    }
  }

  /**
   * Group-lifecycle entry point. Bumps the drone lifecycle generation, aborts
   * any in-flight `setAmsl`, and recreates the drone entity for the new group.
   * Re-entrant calls coalesce onto the in-flight promise.
   */
  const initializeForGroup = async (groupId: string): Promise<void> => {
    if (droneInitInFlight) {
      await droneInitInFlight.catch(() => {})
    }
    const run = async () => {
      droneLifecycle.start(groupId)
      await createDroneEntity()
    }
    droneInitInFlight = run().finally(() => {
      droneInitInFlight = null
    })
    return droneInitInFlight
  }
  const connectHomeMarker = () => {
    if (!viewer.value || !viewer.value.terrainProvider) return
    if (connectingLine.value) {
      removeEntityIfExists(connectingLine.value)
      connectingLine.value = null
    }
    try {
      // Create new line with CallbackProperty
      const entity = viewer.value.entities.add({
        polyline: {
          positions: new CallbackProperty(() => {
            try {
              let dockPosition, position2
              // Handle missing dock configuration
              if (
                !dockConfigurations.value?.dockLongitude ||
                !dockConfigurations.value?.dockLatitude
              ) {
                dockPosition = Cartesian3.fromDegrees(
                  droneLocation.value.longitude,
                  droneLocation.value.latitude,
                  dockAmsl.value || 0,
                )
              } else {
                dockPosition = Cartesian3.fromDegrees(
                  Number(dockConfigurations.value.dockLongitude),
                  Number(dockConfigurations.value.dockLatitude),
                  dockAmsl.value || 0,
                )
              }

              // Handle missing telemetry data
              position2 = Cartesian3.fromDegrees(
                droneLocation.value.longitude,
                droneLocation.value.latitude,
                droneAGL.value,
              )
              return [dockPosition, position2]
            } catch (error) {
              log.error('Error in connecting line callback:', error)
            }
          }, false),
          width: 2,
          material: new PolylineDashMaterialProperty({
            color: Color.WHITE,
            dashLength: 16.0,
          }),
        },
      })
      connectingLine.value = entity.id
      entity.show = false
    } catch (error) {
      log.error('Error creating connecting line:', error)
    }
  }
  const connectAltitudeLine = (callbackPosition: PositionProperty) => {
    if (!viewer.value || !viewer.value.terrainProvider) return
    if (altitudeLine.value) {
      removeEntityIfExists(altitudeLine.value)
      altitudeLine.value = null
    }
    try {
      const entity = viewer.value.entities.add({
        polyline: {
          positions: new CallbackProperty(() => {
            let position1, position2
            try {
              position1 = Cartesian3.fromDegrees(
                droneLocation.value.longitude,
                droneLocation.value.latitude,
                droneAmsl.value,
              )
              position2 = callbackPosition.getValue(CesiumService.viewer.value!.clock.currentTime)
              return [position1, position2]
            } catch (error) {
              log.error('Error in connecting line callback:', error)
            }
          }, false),
          width: 2,
          material: new PolylineDashMaterialProperty({
            color: Color.WHITE,
            dashLength: 16.0,
          }),
        },
        position: new CallbackProperty(() => {
          const position1 = Cartesian3.fromDegrees(
            droneLocation.value.longitude,
            droneLocation.value.latitude,
            droneAmsl.value,
          )
          const position2 = callbackPosition.getValue(CesiumService.viewer.value!.clock.currentTime)

          if (!position2) {
            return position1
          }

          // Calculate midpoint
          return Cartesian3.midpoint(position1, position2, new Cartesian3())
        }, false) as unknown as PositionProperty,
        label: {
          text: new CallbackProperty(() => {
            return `${AGL.value?.toFixed(2) || '0.00'}m`
          }, false),
          ...labelStyle.active,
        },
      })
      altitudeLine.value = entity.id
    } catch (err) {
      log.error('error creating altitude line', err)
    }
  }
  const setupDroneTracking = () => {
    if (!viewer.value || !droneEntity.value) return

    const cameraStoreInstance = cameraStore()

    try {
      const canvas = viewer.value.canvas
      const scene = viewer.value.scene

      // Handle mouse wheel zoom while tracking
      const handleWheel = (event: WheelEvent) => {
        if (!isTrackingDrone.value) return

        // Adjust distance based on wheel delta
        const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9
        const currentDistance = cameraStoreInstance.trackingOffset.distance
        const newDistance = Math.max(20, Math.min(5000, currentDistance * zoomFactor))

        cameraStoreInstance.updateTrackingOffset({ distance: newDistance })
      }

      // Add wheel event listener for zoom
      canvas.addEventListener('wheel', handleWheel, { passive: true })

      // Pre-update event for camera tracking
      const removeEvent = scene.preRender.addEventListener(() => {
        try {
          if (isTrackingDrone.value && droneEntity.value) {
            const entity = viewer.value?.entities.getById(droneEntity.value)
            if (!entity) return

            // Get current drone position
            const position = entity.position?.getValue(
              CesiumService.viewer.value!.clock.currentTime,
            )
            if (position) {
              // Use user-adjustable distance for zoom, fixed heading/pitch
              const distance = cameraStoreInstance.trackingOffset.distance

              // Set camera to follow drone with adjustable zoom
              const offset = new HeadingPitchRange(
                hpRoll.heading,
                -CesiumMath.toRadians(cesiumConfig.cameraTracking.pitchAngle),
                distance,
              )

              CesiumService.viewer.value!.camera.lookAt(position, offset)
            }
          }
        } catch (error) {
          if (error instanceof RuntimeError) {
            appInstance.addNotifications({
              notification: `Camera tracking error: ${error.message}`,
              type: 'error',
              id: 'camera-tracking-error',
            })
            // Disable tracking if we encounter a runtime error
            isTrackingDrone.value = false
          } else {
            // For developer errors, log but keep tracking on
            log.error('Developer error in camera tracking:', error)
          }
        }
      })

      // Add a watcher to reset camera mode when tracking is disabled
      const trackingWatcher = watch(isTrackingDrone, (isTracking, wasTracking) => {
        if (!isTracking && wasTracking && viewer.value) {
          // Reset the camera to free navigation mode when tracking is disabled
          viewer.value.camera.lookAtTransform(Matrix4.IDENTITY)
        }
        if (isTracking && !wasTracking) {
          // Reset zoom to default when tracking is enabled
          cameraStoreInstance.resetTrackingOffset()
        }
      })

      // Return cleanup function
      return () => {
        try {
          removeEvent()
          trackingWatcher()
          canvas.removeEventListener('wheel', handleWheel)
        } catch (error) {
          log.error('Error removing tracking event:', error)
        }
      }
    } catch (error) {
      return () => {} // Return empty cleanup function
    }
  }
  /**
   * Validates that coordinates are valid numbers within reasonable ranges
   */
  const isValidCoordinate = (lat: number, lon: number, alt: number): boolean => {
    if (typeof lat !== 'number' || typeof lon !== 'number' || typeof alt !== 'number') return false
    if (isNaN(lat) || isNaN(lon) || isNaN(alt)) return false
    if (lat === 0 && lon === 0) return false // Likely uninitialized
    if (lat < -90 || lat > 90) return false
    if (lon < -180 || lon > 180) return false
    return true
  }

  /**
   * Initializes the trailing path entity using SampledPositionProperty and PathGraphics.
   * This creates a visual "breadcrumb" trail behind the drone showing its flight history.
   */
  const initializeTrailingPath = () => {
    if (!viewer.value) return

    try {
      // Remove existing trailing path if it exists
      if (trailingPathEntity.value) {
        removeEntityIfExists(trailingPathEntity.value)
        trailingPathEntity.value = null
      }

      // Validate coordinates before creating trail
      const lat = droneLocation.value.latitude
      const lon = droneLocation.value.longitude
      const alt = droneAGL.value

      if (!isValidCoordinate(lat, lon, alt)) {
        log.warn('Cannot initialize trailing path: invalid coordinates', { lat, lon, alt })
        return
      }

      // Create new SampledPositionProperty for interpolated position tracking
      sampledPositionProperty = new SampledPositionProperty()

      // Add initial position sample
      const initialPosition = Cartesian3.fromDegrees(lon, lat, alt)
      const currentTime = viewer.value.clock.currentTime.clone()
      sampledPositionProperty.addSample(currentTime, initialPosition)

      // Create trailing path entity with PathGraphics
      // trailTime is dynamic: full mission length when flying a mission, else config default
      const trailEntity = viewer.value.entities.add({
        position: sampledPositionProperty,
        path: {
          show: true,
          leadTime: cesiumConfig.dronePath.leadTime,
          trailTime: new CallbackProperty(getEffectiveTrailTime, false),
          resolution: cesiumConfig.dronePath.resolution,
          width: cesiumConfig.dronePath.width,
          material: new PolylineGlowMaterialProperty({
            glowPower: cesiumConfig.dronePath.glowPower,
            taperPower: cesiumConfig.dronePath.taperPower,
            color: Color.fromBytes(
              cesiumConfig.dronePath.color[0],
              cesiumConfig.dronePath.color[1],
              cesiumConfig.dronePath.color[2],
            ),
          }),
        },
      })

      trailingPathEntity.value = trailEntity.id
      log.info('Trailing path initialized successfully')
    } catch (error) {
      log.error('Error initializing trailing path:', error)
    }
  }

  /**
   * Adds a new position sample to the trailing path.
   * Should be called whenever the drone position updates.
   */
  const addTrailingPathSample = () => {
    if (!viewer.value) return

    try {
      const lat = droneLocation.value.latitude
      const lon = droneLocation.value.longitude
      const alt = droneAGL.value

      // Validate coordinates before adding sample to prevent visual glitches
      if (!isValidCoordinate(lat, lon, alt)) {
        return
      }
      if (!sampledPositionProperty) {
        initializeTrailingPath()
        return
      }

      const newPosition = Cartesian3.fromDegrees(lon, lat, alt)
      const currentTime = viewer.value.clock.currentTime.clone()
      sampledPositionProperty.addSample(currentTime, newPosition)

      // Prune old samples outside the trail time window (use same dynamic length as path display)
      const trailTime = getEffectiveTrailTime()
      const pruneTime = JulianDate.addSeconds(currentTime, -trailTime - 10, new JulianDate())
      sampledPositionProperty.removeSamples(
        new TimeInterval({ start: new JulianDate(0, 0), stop: pruneTime }),
      )
    } catch (error) {
      log.error('Error adding trailing path sample:', error)
    }
  }

  /**
   * Clears the trailing path by removing the entity and resetting the sampled position property.
   */
  const clearTrailingPath = () => {
    if (trailingPathEntity.value) {
      removeEntityIfExists(trailingPathEntity.value)
      trailingPathEntity.value = null
    }
    sampledPositionProperty = null
  }
  const updateDronePosition = () => {
    if (
      !viewer.value ||
      !droneEntity.value ||
      !telemetry.value?.locationNavigation?.latitude ||
      !telemetry.value?.locationNavigation?.longitude
    )
      return

    try {
      // Update flight data from telemetry
      if (telemetry.value?.aircraftState) {
        // Make sure telemetry data is serializable before passing to workers
        const serializableTelemetry = makeSerializableForWorkers(telemetry.value) as TelemetryData

        // Update FPV camera if active
        if (fpvViewer.value && showFPV.value) {
          // Use serializable data when updating FPV camera to avoid DataCloneError
          if (serializableTelemetry.locationNavigation) {
            const position = Cartesian3.fromDegrees(
              serializableTelemetry.locationNavigation.longitude,
              serializableTelemetry.locationNavigation.latitude,
              Number(droneAmsl.value + (telemetry.value?.aircraftState?.height || 0)) || 0,
            )

            // Create orientation based on drone's attitude
            if (serializableTelemetry.aircraftState) {
              const orientation = HeadingPitchRoll.fromDegrees(
                serializableTelemetry.aircraftState.heading || 0,
                serializableTelemetry.aircraftState.attitude?.pitch || 0,
                serializableTelemetry.aircraftState.attitude?.roll || 0,
              )

              // Update FPV camera through the service
              FPVService.updateFPVCamera(position, orientation)
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof RuntimeError) {
        appInstance.addNotifications({
          notification: `Error updating drone position: ${error.message}`,
          type: 'error',
          id: 'update-drone-position-error',
        })
      } else {
        // For DeveloperError, we log but don't break the UI
        log.error('Developer error in updateDronePosition:', error)
      }
    }
  }
  // Wrapper methods for FPV service
  const initializeFPV = async (fpvElement: Ref<HTMLElement | null>) => {
    try {
      if (!droneLocation.value) return
      return FPVService.initializeFPV(fpvElement, droneLocation.value)
    } catch (e) {
      log.error('initializeFPV: failed to get drone location', e)
    }
  }
  const toggleFpvTileset = async (show: boolean): Promise<boolean> => {
    return FPVService.toggleFpvTileset(show)
  }

  // Delayed watchers setup — installed ONCE per module lifetime.
  const setupWatchers = () => {
    watch(
      () => droneLocation.value,
      async (newv, oldv) => {
        if (!newv?.latitude || !newv?.longitude) return
        addTrailingPathSample()
        try {
          if (!droneAmsl.value) {
            await setAmsl({ signal: droneLifecycle.signal })
            return
          }
          if (
            newv?.latitude.toFixed(5) === oldv?.latitude?.toFixed(5) &&
            newv?.longitude.toFixed(5) === oldv?.longitude?.toFixed(5) &&
            droneAmsl.value !== 0
          ) {
            return
          }
          if (newv?.latitude && newv?.longitude) {
            updateDronePosition()
            await setAmsl({ signal: droneLifecycle.signal })
          }
        } catch (err) {
          if (isAbortError(err)) return
          log.error('droneLocation watcher', err)
        }
      },
    )
  }

  /**
   * Async teardown for device-group switches. Aborts in-flight terrain fetches
   * via the drone lifecycle, then clears drone-specific entities. Watchers +
   * tracking handlers persist by default (they closure-capture reactive refs
   * that naturally track new-group state). On `unmount`, fully tears down.
   */
  const cleanup = async (opts: { unmount?: boolean } = {}): Promise<void> => {
    await droneLifecycle.dispose()

    clearTrailingPath()
    // Drone-specific entities are owned by this composable; remove them here.
    // `assetManager.$reset()` (from useAddAssets.cleanup) also clears these,
    // but calling it twice is idempotent.
    if (droneEntity.value) {
      removeEntityIfExists(droneEntity.value)
      droneEntity.value = null
    }
    if (connectingLine.value) {
      removeEntityIfExists(connectingLine.value)
      connectingLine.value = null
    }
    if (altitudeLine.value) {
      removeEntityIfExists(altitudeLine.value)
      altitudeLine.value = null
    }

    if (opts.unmount) {
      moduleCleanupFunctions.forEach((fn) => {
        try {
          fn()
        } catch (err) {
          log.error('drone module cleanup', err)
        }
      })
      moduleCleanupFunctions.length = 0
      if (trackingCleanup) {
        try {
          trackingCleanup()
        } catch (err) {
          log.error('drone tracking cleanup', err)
        }
        trackingCleanup = null
      }
      droneWatchersInitialized = false
    }
  }
  return {
    droneEntity,
    createDroneEntity,
    initializeForGroup,
    setupDroneTracking,
    showFPV,
    initializeFPV,
    fpvViewer,
    toggleFpvTileset,
    cleanup,
  }
}
